import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { hotComics, categories, siteInfo } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const comic = hotComics.find((c) => c.slug === slug) || hotComics[0];
  const categoryObj = categories.find((c) => c.name === comic.category);
  const categorySlug = categoryObj?.slug || 'all';

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 30) }, (_, i) => ({
    num: comic.chapters - i,
    title: i === 0 ? '最新话' : `第${comic.chapters - i}话`,
    time: i === 0 ? '今天' : i < 3 ? `${i}天前` : `${Math.floor(Math.random() * 30) + 3}天前`,
  }));

  const relatedComics = hotComics.filter((c) => c.id !== comic.id).slice(0, 4);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://homekleans.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: `${'http://homekleans.com'}/category/all`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${comic.category}漫画`,
        item: `${'http://homekleans.com'}/category/${categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: comic.title,
        item: 'http://homekleans.com/',
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: comic.title,
    headline: `${comic.title} - 叮当漫画网无删减版`,
    description: comic.description,
    url: 'http://homekleans.com/',
    image: comic.cover,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      logo: { '@type': 'ImageObject', url: `${'http://homekleans.com'}/logo.png` },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: comic.chapters,
    },
    genre: comic.category,
    inLanguage: 'zh-CN',
    isAccessibleForFree: true,
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <Navbar />
      <main>
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20 pb-16">
          <Breadcrumb items={[
            { name: '分类浏览', path: '/category/all' },
            { name: `${comic.category}漫画`, path: `/category/${categorySlug}` },
            { name: comic.title },
          ]} />

          <article className="mt-4">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 叮当漫画网`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-black text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                </div>

                <div className="flex items-center gap-6 mb-6 p-4 bg-background-100 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-black text-primary-500">{comic.rating}</div>
                    <div className="text-xs text-foreground-500 mt-0.5">评分</div>
                  </div>
                  <div className="w-px h-10 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-foreground-900">{comic.chapters}</div>
                    <div className="text-xs text-foreground-500 mt-0.5">总话数</div>
                  </div>
                  <div className="w-px h-10 bg-background-300"></div>
                  <div className="text-center">
                    <div className="text-sm font-semibold text-accent-600">{comic.updateTime}</div>
                    <div className="text-xs text-foreground-500 mt-0.5">最近更新</div>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/comic/${comic.slug}/read`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <button className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-700 font-medium text-sm rounded-full hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {mockChapters.map((ch) => (
                  <Link
                    key={ch.num}
                    to={`/comic/${comic.slug}/read/${ch.num}`}
                    className="flex items-center justify-between p-3.5 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 group cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.time}</span>
                  </Link>
                ))}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.slug}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-200">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 叮当漫画网`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 whitespace-nowrap overflow-hidden text-ellipsis">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{rc.category} · {rc.chapters}话</p>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}